import type { LanguageCode } from "@/config/i18n";
import type {
  LibraryMovie,
  LibrarySnapshot,
  LibrarySort,
  LibraryView,
  MovieId,
} from "@/types/library";

/**
 * Pure helpers for the library pages: turning a snapshot into the list a
 * page shows, filtering, sorting and the stats header.
 */

export interface LibraryItem {
  movieId: MovieId;
  /** Catalog metadata, or null when unknown (rendered as a minimal card). */
  movie: LibraryMovie | null;
  /** When it was added / watched / rated (depending on the view). */
  date: string;
  /** The user's rating, if any. */
  rating: number | null;
  inWatchlist: boolean;
  watched: boolean;
}

export const DEFAULT_LIBRARY_SORT: LibrarySort = "date-desc";

/** Sort options offered on each page, in menu order. */
export const LIBRARY_SORTS: Record<LibraryView, readonly LibrarySort[]> = {
  watchlist: [
    "date-desc",
    "date-asc",
    "title-asc",
    "year-desc",
    "year-asc",
    "imdb-desc",
  ],
  watched: [
    "date-desc",
    "date-asc",
    "title-asc",
    "year-desc",
    "year-asc",
    "rating-desc",
    "rating-asc",
    "imdb-desc",
  ],
  ratings: [
    "date-desc",
    "date-asc",
    "rating-desc",
    "rating-asc",
    "title-asc",
    "year-desc",
    "year-asc",
    "imdb-desc",
  ],
};

/** Read a sort from the URL, falling back to the default for unknown values. */
export function parseLibrarySort(
  value: string | null | undefined,
  view: LibraryView,
): LibrarySort {
  return LIBRARY_SORTS[view].includes(value as LibrarySort)
    ? (value as LibrarySort)
    : DEFAULT_LIBRARY_SORT;
}

const LOCALES: Record<LanguageCode, string> = {
  en: "en-US",
  ko: "ko-KR",
  ja: "ja-JP",
};

export const localeFor = (lang: LanguageCode) => LOCALES[lang] ?? "en-US";

/** The entries of one collection, joined with their metadata. */
export function buildLibraryItems(
  view: LibraryView,
  library: LibrarySnapshot,
  movies: Record<MovieId, LibraryMovie>,
): LibraryItem[] {
  const dated: Array<[MovieId, string]> =
    view === "watchlist"
      ? Object.entries(library.watchlist).map(([id, e]) => [id, e.addedAt])
      : view === "watched"
        ? Object.entries(library.watched).map(([id, e]) => [id, e.watchedAt])
        : Object.entries(library.ratings).map(([id, e]) => [id, e.ratedAt]);

  return dated.map(([movieId, date]) => ({
    movieId,
    movie: movies[movieId] ?? null,
    date,
    rating: library.ratings[movieId]?.rating ?? null,
    inWatchlist: Object.prototype.hasOwnProperty.call(
      library.watchlist,
      movieId,
    ),
    watched: Object.prototype.hasOwnProperty.call(library.watched, movieId),
  }));
}

/** Lowercase, width-insensitive form for matching titles (e.g. ｆ → f). */
const normalize = (value: string) => value.normalize("NFKC").toLowerCase();

/** Keep items whose title contains `query` (ignoring case and width). */
export function filterLibraryItems(
  items: LibraryItem[],
  query: string,
): LibraryItem[] {
  const needle = normalize(query.trim());

  if (!needle) return items;

  return items.filter((item) =>
    normalize(item.movie?.title ?? "").includes(needle),
  );
}

const time = (iso: string) => {
  const value = Date.parse(iso);

  return Number.isFinite(value) ? value : 0;
};

/** Compare with nulls always last, whatever the direction. */
function compareNullable(
  a: number | null,
  b: number | null,
  direction: 1 | -1,
): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;

  return (a - b) * direction;
}

export function sortLibraryItems(
  items: LibraryItem[],
  sort: LibrarySort,
  lang: LanguageCode,
): LibraryItem[] {
  const collator = new Intl.Collator(localeFor(lang), {
    sensitivity: "base",
    numeric: true,
  });
  const byTitle = (a: LibraryItem, b: LibraryItem) => {
    // Movies without metadata go last.
    if (!a.movie || !b.movie) return a.movie ? -1 : b.movie ? 1 : 0;

    return collator.compare(a.movie.title, b.movie.title);
  };
  const byDate = (a: LibraryItem, b: LibraryItem) =>
    time(a.date) - time(b.date);
  const primary = (a: LibraryItem, b: LibraryItem): number => {
    switch (sort) {
      case "date-desc":
        return -byDate(a, b);
      case "date-asc":
        return byDate(a, b);
      case "title-asc":
        return byTitle(a, b);
      case "year-desc":
        return compareNullable(
          a.movie?.releaseYear ?? null,
          b.movie?.releaseYear ?? null,
          -1,
        );
      case "year-asc":
        return compareNullable(
          a.movie?.releaseYear ?? null,
          b.movie?.releaseYear ?? null,
          1,
        );
      case "rating-desc":
        return compareNullable(a.rating, b.rating, -1);
      case "rating-asc":
        return compareNullable(a.rating, b.rating, 1);
      case "imdb-desc":
        return compareNullable(
          a.movie?.imdbRating ?? null,
          b.movie?.imdbRating ?? null,
          -1,
        );
    }
  };

  // Ties: most recent first, then by title, so the order is stable.
  return [...items].sort(
    (a, b) =>
      primary(a, b) ||
      -byDate(a, b) ||
      byTitle(a, b) ||
      a.movieId.localeCompare(b.movieId),
  );
}

export type LibraryRank =
  "curious" | "fan" | "enthusiast" | "devotee" | "scholar" | "treasure";

/** A playful rank for how much of the filmography someone has seen. */
export function rankFor(watched: number, total: number | null): LibraryRank {
  if (!total) return "curious";
  if (watched >= total) return "treasure";
  const share = watched / total;

  if (share >= 0.75) return "scholar";
  if (share >= 0.5) return "devotee";
  if (share >= 0.25) return "enthusiast";
  if (share >= 0.1) return "fan";

  return "curious";
}

export interface LibraryStats {
  watchlistCount: number;
  watchedCount: number;
  ratedCount: number;
  /** Watched movies that are part of the browsable catalog. */
  watchedInCatalog: number;
  /** Size of the browsable catalog, when known. */
  total: number | null;
  /** 0-100, when the total is known. */
  percent: number | null;
  /** Average of the user's ratings (one decimal), or null. */
  averageRating: number | null;
  rank: LibraryRank;
}

export function computeLibraryStats(
  library: LibrarySnapshot,
  catalog: { total: number; ids: MovieId[] } | null,
): LibraryStats {
  const watchedIds = Object.keys(library.watched);
  const ratings = Object.values(library.ratings).map((entry) => entry.rating);
  const inCatalog = catalog ? new Set(catalog.ids) : null;
  const watchedInCatalog = inCatalog
    ? watchedIds.filter((id) => inCatalog.has(id)).length
    : watchedIds.length;
  const total = catalog && catalog.total > 0 ? catalog.total : null;
  const sum = ratings.reduce((acc, rating) => acc + rating, 0);

  return {
    watchlistCount: Object.keys(library.watchlist).length,
    watchedCount: watchedIds.length,
    ratedCount: ratings.length,
    watchedInCatalog,
    total,
    percent:
      total === null
        ? null
        : Math.min(100, Math.round((watchedInCatalog / total) * 100)),
    averageRating: ratings.length
      ? Math.round((sum / ratings.length) * 10) / 10
      : null,
    rank: rankFor(watchedInCatalog, total),
  };
}
